export const WEAPON = {
  id: 'dmr',
  label: 'SR-25K',
  class: 'dmr',
  caliber: '7.62x51',
  /** Premium mid-to-late-game unlock: pricier than the SMG's 800g, cheaper
   *  than the LMG will be, in line with the sniper's 2000g since it shares
   *  the same 7.62 chambering and a magnified optic. */
  price: 1900,
  /* --- fire control ---
   * Semi-auto only — no full-auto option, which is the point of the class
   * (see CONTRACT.md: lmg/dmr fire exactly like a carbine in tryFire(), the
   * distinction is the silhouette/handling, not special-cased mechanics).
   * `rpm` stands in for the max semi-auto cyclic rate a shooter can run this
   * gun at (trigger reset + recoil recovery), not a mechanical fire rate:
   * a gas-operated 7.62 semi comfortably runs 550-600 rounds/min of aimed
   * fire, faster than the bolt gun's ~45 rpm manual-cycle stand-in and
   * necessarily capped below the carbine's 800 rpm auto rate. */
  rpm: 550,
  modes: ['semi'],
  burstCount: 1,
  burstRpm: 550,
  burstDelay: 0.1,
  /* --- ammunition ---
   * 20-round AR-10/SR-25-pattern box, real capacity for the platform (the
   * carbine's 30-round 5.56 mag holds more, smaller rounds; the bolt gun's
   * 5-round mag is a single-stack magnum-length feed). 100 rounds reserve is
   * five spare mags, the same ratio the carbine's 210/30 = 7x uses scaled to
   * a lower-capacity, harder-recoiling platform. */
  magSize: 20,
  reserve: 100,
  /* --- terminal ballistics ---
   * 7.62x51 M118LR-class ball from a 16.1" (SR-25K) barrel: real fielded
   * DMRs (Mk11 Mod 0/SR-25) run this barrel length at ~750-790 m/s, short of
   * the bolt gun's longer 660 mm barrel (830 m/s) but well above the
   * carbine's 5.56 out of a 14.5" tube (880 m/s of a much lighter, faster,
   * less energetic bullet). Damage/range sit in the gap between carbine and
   * bolt-action sniper by design: enough to be worth the mag-size and
   * recoil tradeoff, nowhere near the sniper's one-shot-kill numbers or its
   * glacial 45 rpm cycle. */
  muzzleVelocity: 780,
  damage: 58,
  penetration: 1.2,
  dropoff: 0.66,
  maxRange: 620,
  dragK: 0.25,
  tracerEvery: 2,
  /* --- accuracy (degrees) ---
   * Hip spread sits above the carbine's (a heavier gun with a magnified
   * optic is not meant to be hip-fired accurately) but well under the bolt
   * gun's (it still has a pistol grip and a semi-auto action, not a
   * shouldered-only bolt rifle). ADS spread is tighter than the carbine's —
   * this is the marksman's rifle — but not the bolt gun's near-zero, since
   * the gas action introduces shot-to-shot wobble a fixed breech does not. */
  spreadHip: 2.6,
  spreadAds: 0.12,
  spreadPerShot: 0.45,
  spreadMax: 4.2,
  spreadDecay: 4.8,
  /* --- recoil ---
   * Same cartridge as the bolt gun, so the impulse per shot is close to it —
   * a little softer, since a gas gun bleeds some energy into cycling the
   * action instead of transmitting all of it straight into the shoulder —
   * and well above the carbine's 5.56 recoil. `patternLength` is tied to
   * `magSize` (20), matching the convention rifle.js (30) and sniper.js (5)
   * both use: the deterministic pattern repeats once per magazine. */
  recoil: {
    pitch: 0.013,
    yaw: 0.002,
    kickBack: 0.024,
    kickUp: 0.0095,
    roll: 0.037,
    punch: 0.46,
    freq: 7.5,
    damping: 0.46,
    patternLength: 20,
    patternSeed: 0x5d25a1,
    climbShape: [1.3, 1.15, 1.05, 1.0],
    drift: 0.4,
  },
  /* --- handling (seconds) ---
   * Slower to shoulder and reload than the carbine (heavier gun, a bigger
   * mag to seat) but faster than the bolt gun everywhere it matters for a
   * semi-auto: ADS is quicker than working a bolt-gun's scope-to-eye
   * distance, reload is quicker than the 5-round single-stack. adsFov sits
   * between the carbine's near-1x red dot and the bolt gun's high-power
   * scope, matching a real DMR optic's more modest magnification. */
  adsTime: 0.28,
  adsFov: 0.55,
  viewFov: 0.85,
  reloadTac: 2.3,
  reloadEmpty: 3.1,
  inspectTime: 3.3,
  drawTime: 0.66,
  holsterTime: 0.42,
  /* --- pose ---
   * SOLVED FROM THE BORE AXIS, same method as defs/rifle.js (read that
   * derivation first) and defs/sniper.js (same optic-type family). The
   * screen-space projection is: cameraPoint = hipPos + R(hipRot)*localPoint,
   * then at a 60 deg vertical FOV (half-height 0.5774|z|, half-width
   * 1.0264|z|) screenX/Y follow the same formulas rifle.js's comment derives
   * and verifies against its own numbers.
   *
   * hipRot is the same convergence/roll family as rifle.js/sniper.js — the
   * 4 deg left / ~2.9 deg nose-down convergence onto the crosshair and the
   * ~7.6 deg roll that turns the rail deck edge-on are properties of the
   * VIEW FRUSTUM and the desired composition (muzzle up-left heading to
   * centre, rollmark flank toward the camera), not of this gun's own
   * dimensions, so they carry over with only the small adjustment sniper.js
   * itself already made (0.079/-0.132 vs rifle's 0.081/-0.135).
   *
   * What is THIS gun's own solve is hipPos.z: with this weapon's actual
   * muzzle at local (0, 0.078, -0.678) and scope ocular at local
   * (0, 0.150, 0.0125) (see models/dmr.js), z=-0.315 puts:
   *   muzzle  -> (1036, 684)   up-left of screen centre, heading for it
   *   optic   -> (1364, 663)   below and right of the muzzle
   *   magSeat -> (1245, 862)   lower-right frame, same quadrant rifle.js's
   *                            magwell mouth (1268, 870) lands in
   * i.e. the same read rifle.js's derivation calls out as correct, verified
   * with the same projection formula (checked in scratch — see PR notes).
   * z=-0.315 sits between the carbine's -0.30 and the bolt gun's -0.335,
   * which is exactly where this gun's own overall length falls. */
  hipPos: [0.114, -0.19, -0.315],
  hipRot: [-0.05, 0.079, -0.132],
  adsCant: [0, 0, 0.0035],
  /* Eye to the rear lens, MEASURED FROM THE ADS FRAME the same way
   * rifle.js's eyeRelief comment derives it: interpolated between the
   * carbine's 52 mm tube / 115 mm relief and the bolt gun's 120 mm tube /
   * 135 mm relief for this optic's own 95 mm tube, so the ocular-to-housing
   * ratio lands in between rather than duplicating either weapon's optic. */
  eyeRelief: 0.125,
  /* Sprint/low-ready: carried over by the same delta as the hip pose, same
   * convention rifle.js's comment describes, averaged between the carbine's
   * and the bolt gun's own deltas since this gun sits between them in both
   * length and weight. sprintRot/lowReadyRot are identical across all three
   * 7.62/5.56 shouldered rifles in this family — they describe how the gun
   * is carried, not the gun itself. */
  sprintPos: [0.09, -0.266, -0.28],
  sprintRot: [-0.4, 0.6, 0.2],
  lowReadyPos: [0.112, -0.285, -0.295],
  lowReadyRot: [-0.46, 0.125, -0.09],
  swayScale: 1.03,
  bobScale: 0.92,
  magLen: 0.235,
};

export const loadModel = () => import('../models/dmr.js').then((m) => m.buildDmr);
